import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import type { Property } from '../types';

/**
 * Calculate investment score from available property data.
 * Used as fallback when API doesn't provide investmentScore.
 */
function calculateScore(p: Property): number {
  let score = 50; // Base score

  // Cap rate bonus (8%+ = excellent)
  const capRate = p.capRate ?? 0;
  if (capRate >= 10) score += 30;
  else if (capRate >= 8) score += 25;
  else if (capRate >= 6) score += 15;
  else if (capRate >= 4) score += 5;
  else if (capRate < 3) score -= 10;

  // Cash flow bonus
  const cashFlow = p.monthlyCashFlow ?? 0;
  if (cashFlow > 500) score += 20;
  else if (cashFlow > 300) score += 15;
  else if (cashFlow > 100) score += 10;
  else if (cashFlow > 0) score += 5;
  else if (cashFlow < -200) score -= 20;
  else if (cashFlow < 0) score -= 10;

  // Bedroom score (3-4 optimal for rentals)
  const beds = p.beds ?? 0;
  if (beds === 3 || beds === 4) score += 10;
  else if (beds === 2 || beds === 5) score += 5;

  // Cash-on-cash return bonus
  const coc = p.cashOnCash ?? 0;
  if (coc >= 15) score += 10;
  else if (coc >= 10) score += 5;
  else if (coc < 0) score -= 10;

  return Math.min(100, Math.max(0, Math.round(score)));
}

interface UsePropertiesResult {
  properties: Property[];
  loading: boolean;
  error: string | null;
  loadMarket: (market: string) => Promise<void>;
}

export function useProperties(initialMarket?: string): UsePropertiesResult {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadMarket = useCallback(async (market: string) => {
    if (!market) return;

    setLoading(true);
    setError(null);
    try {
      const data = await api.getMarketProperties(market);
      const props = (data.properties || []).map((p) => {
        const mapped = {
          ...p,
          market,
          // Map API field names to frontend field names
          dom: p.daysOnMarket ?? p.dom,
        };
        // Map score from various possible API field names
        mapped.score = p.investmentScore ?? p.score ?? p.finalScore ?? p.propertyScore ?? calculateScore(mapped);
        return mapped;
      });
      setProperties(props);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load properties');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialMarket) {
      loadMarket(initialMarket);
    }
  }, [initialMarket, loadMarket]);

  return { properties, loading, error, loadMarket };
}

export interface StoredMarket {
  market: string;
  name: string;
  state_code: string;
  count: number;
  landlord_score?: number;
  gross_yield?: number;
  zhvi_current?: number;
}

interface UseStoredMarketsResult {
  markets: StoredMarket[];
  loading: boolean;
  error: string | null;
}

/**
 * Generate a URL-friendly slug from market name
 * e.g., "Indianapolis, IN" → "indianapolis"
 */
function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .split(',')[0]
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

export function useStoredMarkets(): UseStoredMarketsResult {
  const [markets, setMarkets] = useState<StoredMarket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMarkets = async () => {
      try {
        // Use /api/markets to get ALL available markets (not just crawled ones)
        const data = await api.getMarkets();
        const mappedMarkets = (data.markets || []).map((m) => ({
          market: generateSlug(m.name),
          name: m.name,
          state_code: m.state_code || m.state || 'Other',
          count: m.property_count || 0,
          landlord_score: m.landlord_score,
          gross_yield: m.gross_yield_annual,
          zhvi_current: m.zhvi_current,
        }));
        setMarkets(mappedMarkets);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load markets');
      } finally {
        setLoading(false);
      }
    };

    fetchMarkets();
  }, []);

  return { markets, loading, error };
}
